// Local mode: the whole state in localStorage on this device. Used for the preview and
// for development without Supabase. Other tabs stay in sync via the `storage` event.

import type { PersonId } from '@shared/types.ts';
import type { AppSettings, AppState } from './state.ts';
import type { Collection, DataSource, PersonPatch, Row, SessionInfo } from './source.ts';
import { initialRealState, sampleState } from './seed.ts';
import { emptyState } from './state.ts';

const KEY = 'natangelo:v1';

/** localStorage can be missing or throw on access (private mode, sandboxed frames). */
export function safeLocalStorage(): Storage | undefined {
  try {
    if (typeof localStorage === 'undefined') return undefined;
    const probe = '__natangelo_probe__';
    localStorage.setItem(probe, '1');
    localStorage.removeItem(probe);
    return localStorage;
  } catch {
    return undefined;
  }
}

function idOf(row: unknown): string {
  return String((row as { id: string }).id);
}

export class LocalSource implements DataSource {
  readonly kind = 'local' as const;
  private state: AppState | undefined;

  constructor(
    private readonly inboxAddress: string,
    private readonly useSampleData: boolean,
    private readonly storage: Storage | undefined = safeLocalStorage(),
  ) {}

  private read(): AppState | undefined {
    try {
      const raw = this.storage?.getItem(KEY);
      if (!raw) return undefined;
      const parsed = JSON.parse(raw) as AppState;
      // Fill fields added in later versions.
      return { ...emptyState(this.inboxAddress), ...parsed };
    } catch {
      return undefined;
    }
  }

  private write(): void {
    try {
      this.storage?.setItem(KEY, JSON.stringify(this.state));
    } catch {
      // Storage full or unavailable (private mode): keep working in memory.
    }
  }

  async load(): Promise<AppState> {
    this.state = this.read() ?? (this.useSampleData ? sampleState(this.inboxAddress) : initialRealState(this.inboxAddress));
    this.write();
    return structuredClone(this.state);
  }

  async upsert<C extends Collection>(collection: C, rows: Row<C>[]): Promise<void> {
    if (!this.state) await this.load();
    const current = this.state![collection] as Row<C>[];
    const byId = new Map(current.map((r) => [idOf(r), r]));
    for (const r of rows) byId.set(idOf(r), structuredClone(r));
    (this.state as unknown as Record<string, unknown>)[collection] = [...byId.values()];
    this.write();
  }

  async remove(collection: Collection, ids: string[]): Promise<void> {
    if (!this.state) await this.load();
    const drop = new Set(ids);
    (this.state as unknown as Record<string, unknown>)[collection] = (this.state![collection] as unknown[]).filter((r) => !drop.has(idOf(r)));
    this.write();
  }

  async saveSettings(settings: AppSettings): Promise<void> {
    if (!this.state) await this.load();
    this.state!.settings = settings;
    this.write();
  }

  async updatePerson(id: PersonId, patch: PersonPatch): Promise<void> {
    if (!this.state) await this.load();
    this.state!.people = this.state!.people.map((p) => (p.id === id ? { ...p, ...patch } : p));
    this.write();
  }

  async saveMe(me: AppState['me']): Promise<void> {
    if (!this.state) await this.load();
    this.state!.me = me;
    this.write();
  }

  subscribe(onRemoteChange: (collection: Collection | 'all') => void): () => void {
    if (typeof window === 'undefined') return () => {};
    const handler = (e: StorageEvent) => {
      if (e.key !== KEY) return;
      this.state = this.read();
      onRemoteChange('all');
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }

  async reload<C extends Collection>(collection: C): Promise<Row<C>[]> {
    this.state = this.read() ?? this.state;
    return structuredClone(this.state![collection]) as Row<C>[];
  }

  async session(): Promise<SessionInfo> {
    return { signedIn: true };
  }

  /** Wipe this device's data (Settings -> reset). */
  reset(): void {
    try {
      this.storage?.removeItem(KEY);
    } catch {
      // ignore
    }
    this.state = undefined;
  }
}
